import { useCallback, useEffect, useRef, useState } from "react";
import { HomeScreen } from "./components/HomeScreen";
import { Editor } from "./components/Editor";
import { Diagnostics } from "./components/Diagnostics";
import { readAudioMetadata, isAcceptedAudioFile } from "./media/input";
import {
  cleanExpiredProjects,
  loadMostRecentProject,
  saveProject,
} from "./storage/database";
import type { DiagnosticsRecord, EditingProject } from "./types/project";
import { APP_VERSION, PROJECT_TTL_MS } from "./types/project";
import { usePwaUpdate } from "./pwa/update";

type ActiveProject = { project: EditingProject; file: File };

export default function App() {
  const [resume, setResume] = useState<ActiveProject | null>(null);
  const [active, setActive] = useState<ActiveProject | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [diagnostics, setDiagnostics] = useState<DiagnosticsRecord>({});
  const update = usePwaUpdate();
  const saveTimer = useRef<number | null>(null);

  useEffect(() => {
    void (async () => {
      await cleanExpiredProjects().catch(() => undefined);
      const recent = await loadMostRecentProject().catch(() => null);
      setResume(recent);
      setBusy(false);
    })();
  }, []);

  useEffect(
    () => () => {
      if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    },
    [],
  );

  const importFiles = async (files: FileList): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      let persistenceWarning: string | null = null;
      const accepted = [...files].filter(isAcceptedAudioFile);
      if (accepted.length === 0)
        throw new Error(
          "M4A / AAC / MP3 / WAVの音声ファイルを選択してください",
        );
      await navigator.storage?.persist?.().catch(() => false);
      let first: ActiveProject | null = null;
      for (const file of accepted) {
        const metadata = await readAudioMetadata(file);
        const now = Date.now();
        const project: EditingProject = {
          ...metadata,
          id: crypto.randomUUID(),
          startFrame: 0,
          endFrame: metadata.durationFrames,
          playheadFrame: 0,
          playbackRate: 1,
          title: metadata.fileName.replace(/\.[^.]+$/, ""),
          suppressClicks: true,
          viewport: {
            centerFrame: 0,
            visibleFrames: Math.min(
              metadata.durationFrames,
              metadata.sampleRate * 30,
            ),
          },
          createdAt: now,
          updatedAt: now,
          sourceExpiresAt: now + PROJECT_TTL_MS,
        };
        const storage = await navigator.storage?.estimate?.();
        const remaining =
          (storage?.quota ?? Number.MAX_SAFE_INTEGER) - (storage?.usage ?? 0);
        const persistSource = file.size * 1.25 < remaining;
        try {
          await saveProject(project, persistSource ? file : undefined);
        } catch {
          // Some Safari/WebKit versions can edit a selected File but cannot
          // structured-clone it into IndexedDB. Keep the editing session usable
          // and retry with metadata only; reopening will require reselecting it.
          await saveProject(project).catch(() => undefined);
          persistenceWarning =
            "元音声を一時保存できません。編集中の操作は可能です。再起動後は音声を再選択してください。";
        }
        first ??= { project, file };
      }
      if (!first) throw new Error("音声を読み込めませんでした");
      setResume(first);
      setActive(first);
      setDiagnostics({
        inputCodec: first.project.codec,
        sampleRate: first.project.sampleRate,
        channels: first.project.channels,
        inputDurationSeconds:
          first.project.durationFrames / first.project.sampleRate,
      });
      if (persistenceWarning) reportError(persistenceWarning);
    } catch (caught) {
      const message = messageOf(caught);
      setError(message);
      setDiagnostics((current) => ({ ...current, lastError: message }));
    } finally {
      setBusy(false);
    }
  };

  const projectChanged = useCallback((project: EditingProject) => {
    setActive((current) => (current ? { ...current, project } : current));
    setResume((current) =>
      current?.project.id === project.id ? { ...current, project } : current,
    );
    if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(
      () =>
        void saveProject(project).catch((caught: unknown) => {
          const message = messageOf(caught);
          setError(`編集状態を保存できませんでした: ${message}`);
          setDiagnostics((current) => ({ ...current, lastError: message }));
        }),
      250,
    );
  }, []);

  const reportError = useCallback((message: string) => {
    setError(message);
    setDiagnostics((current) => ({ ...current, lastError: message }));
    window.setTimeout(
      () => setError((current) => (current === message ? null : current)),
      8000,
    );
  }, []);

  const updateDiagnostics = useCallback((patch: Partial<DiagnosticsRecord>) => {
    setDiagnostics((current) => ({ ...current, ...patch }));
  }, []);

  return (
    <div className="app-root">
      {update.available && (
        <div className="update-banner" role="status">
          <span>新しいバージョンがあります</span>
          <button type="button" onClick={update.apply}>
            更新
          </button>
        </div>
      )}
      {error && active && (
        <div className="global-toast" role="alert">
          {error}
          <button
            type="button"
            aria-label="閉じる"
            onClick={() => setError(null)}
          >
            ×
          </button>
        </div>
      )}

      {active ? (
        <Editor
          project={active.project}
          file={active.file}
          onProjectChange={projectChanged}
          onBack={() => setActive(null)}
          onDiagnosticsChange={updateDiagnostics}
          onError={reportError}
        />
      ) : (
        <HomeScreen
          resume={resume}
          busy={busy}
          error={error}
          onImport={(files) => void importFiles(files)}
          onResume={() => resume && setActive(resume)}
          onDiagnostics={() => setShowDiagnostics(true)}
        />
      )}

      <button
        type="button"
        className="version-pill"
        onClick={() => setShowDiagnostics(true)}
        aria-label="Diagnosticsを開く"
      >
        VoiceCutter Web v{APP_VERSION}
      </button>

      {showDiagnostics && (
        <Diagnostics
          project={active?.project ?? resume?.project}
          record={diagnostics}
          serviceWorkerVersion={update.serviceWorkerVersion}
          onClose={() => setShowDiagnostics(false)}
        />
      )}
    </div>
  );
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
